/**
 * `CsatRating` — post-chat CSAT capture rendered on the {@link EndedScreen}
 * (ARCHITECTURE §7). An overall 1-5 star control plus an optional comment, posted via the public
 * widget API (`api.widget.submitRating`, which carries the still-live session token). When the org
 * enables dual ratings, a SECOND star row (agent/service) is shown and both are required before
 * submit. On success it swaps to a thank-you state that offers an "Edit my feedback" affordance —
 * clicking it returns to the SAME form, still pre-filled with whatever was just submitted (the
 * rating/agentRating/comment state is never cleared), so a visitor can correct and resubmit. The
 * backend's `set_rating` is a partial update: a field genuinely OMITTED from the request leaves
 * the previously-saved value untouched, while an explicit empty/`null` value clears it. That
 * distinction matters for the comment box specifically — see `submit`'s `commentToSend` for the
 * first-submit-vs-edit rule this component uses to pick which one to send.
 * A submit failure surfaces inline and leaves the form in place for a retry.
 *
 * EndedScreen only mounts this once both `cid` and `sessionId` are known, so the props are plain
 * required strings (no crash path here).
 */

import { useEffect, useRef, useState } from 'react';
import { getWidgetApi } from '../config/loadWidgetConfig';

export interface CsatRatingProps {
  /** Embed id (`ORG_ID-WIDGET_ID`) the rating is scoped to. */
  cid: string;
  /** The just-ended chat session's id. */
  sessionId: string;
  /** When true, also collect a separate agent/service rating (both required). */
  dualRating?: boolean;
}

type SubmitStatus = 'idle' | 'submitting' | 'done';

const STARS = [1, 2, 3, 4, 5] as const;

/** One labelled 1-5 star row with hover preview. */
function StarRow({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  disabled: boolean;
  onChange: (v: number) => void;
}) {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className="flex flex-col items-center gap-1">
      <p className="text-sm text-lc-text/70">{label}</p>
      <div className="flex items-center gap-1" role="radiogroup" aria-label={`${label}: 1 to 5 stars`}>
        {STARS.map((v) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={value === v}
            aria-label={`${v} star${v === 1 ? '' : 's'}`}
            disabled={disabled}
            onMouseEnter={() => setHover(v)}
            onMouseLeave={() => setHover(0)}
            onFocus={() => setHover(v)}
            onBlur={() => setHover(0)}
            onClick={() => onChange(v)}
            className={`text-2xl leading-none transition-colors disabled:opacity-50 ${
              v <= active ? 'text-lc-primary' : 'text-lc-text/25'
            }`}
          >
            ★
          </button>
        ))}
      </div>
    </div>
  );
}

export function CsatRating({ cid, sessionId, dualRating = false }: CsatRatingProps) {
  const [rating, setRating] = useState(0);
  const [agentRating, setAgentRating] = useState(0);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  // Once true, the form is in "edit my feedback" mode (a resubmit) rather than a first submit —
  // purely cosmetic (button label); the request itself is the same `submitRating` call either way.
  const [hasSubmittedOnce, setHasSubmittedOnce] = useState(false);

  // Guard against setState after the screen unmounts (e.g. panel closed mid-submit).
  // Re-arm on every (re)mount so React 18 StrictMode's mount→unmount→remount in dev doesn't
  // leave the ref stuck at false (which would freeze the form on "Submitting…").
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const incomplete = rating < 1 || (dualRating && agentRating < 1);

  const submit = async (): Promise<void> => {
    if (incomplete || status === 'submitting') return;
    setStatus('submitting');
    setError(null);
    try {
      const trimmed = comment.trim();
      // `submitRating` → shared `httpClient` JSON.stringify DROPS keys whose value is `undefined`
      // but keeps an explicit `''`/`null` — and the backend's partial-update `set_rating` treats
      // "key absent" as "leave the stored value alone" vs. "key present" as "apply this value
      // (even if blank)". So the two cases need different signals:
      //   - FIRST submit, box left empty: nothing was ever saved, so omitting the key (undefined)
      //     is fine — there's nothing to preserve or clear.
      //   - EDIT/resubmit, box emptied: a previous comment IS saved, and the visitor blanking the
      //     box means "clear it" — send an explicit '' so `model_fields_set` sees the key and the
      //     backend actually nulls the stored comment, instead of silently no-op'ing.
      const commentToSend = trimmed.length > 0 ? trimmed : hasSubmittedOnce ? '' : undefined;
      await getWidgetApi().widget.submitRating(
        cid,
        sessionId,
        rating,
        commentToSend,
        dualRating && agentRating >= 1 ? agentRating : undefined,
      );
      if (mounted.current) {
        setStatus('done');
        setHasSubmittedOnce(true);
      }
    } catch {
      if (mounted.current) {
        setStatus('idle');
        setError('Could not submit your rating. Please try again.');
      }
    }
  };

  if (status === 'done') {
    return (
      <div className="flex flex-col items-center gap-2">
        <p className="text-sm text-lc-text/80" role="status">
          Thanks for your feedback!
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="text-xs font-medium text-lc-primary underline-offset-2 hover:underline"
        >
          Edit my feedback
        </button>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-xs flex-col items-center gap-3">
      <p className="text-sm text-lc-text/70">How was your experience?</p>

      <StarRow
        label={dualRating ? 'Overall' : ''}
        value={rating}
        disabled={status === 'submitting'}
        onChange={setRating}
      />
      {dualRating ? (
        <StarRow
          label="Agent / service"
          value={agentRating}
          disabled={status === 'submitting'}
          onChange={setAgentRating}
        />
      ) : null}

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        disabled={status === 'submitting'}
        placeholder="Add a comment (optional)"
        className="min-h-[3rem] w-full rounded-lc bg-black/5 px-3 py-2 text-left text-sm text-lc-text outline-none focus:ring-2 focus:ring-lc-primary/40 disabled:opacity-50"
      />

      {error ? (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        onClick={submit}
        disabled={incomplete || status === 'submitting'}
        className="rounded-lc bg-lc-primary px-4 py-2 text-sm font-medium text-lc-on-primary disabled:opacity-50"
      >
        {status === 'submitting'
          ? 'Submitting…'
          : hasSubmittedOnce
            ? 'Update feedback'
            : 'Submit feedback'}
      </button>
    </div>
  );
}
